import type { FrequencyFile, FrequencyWarning } from '@wheere/api-site';

/**
 * Fichier de fréquences d'un site ou d'un preset (ADR-039) : `freq` (Hz), `freq_delta` (un par
 * antenne, le premier vaut 0, positifs et strictement croissants), `theta` (entiers 0-3600, un par fréquence, commun à toutes les
 * antennes). L'antenne Ai (ordre gateway puis antenne) émet `freq[k] + freq_delta[i]` avec
 * `theta[k]` ; son DAC est le RF OUT choisi, sinon son rang dans la gateway.
 *
 * Fonctions pures : le serveur fait foi pour les contrôles à l'import (refus 422 chiffrés) ;
 * le front recalcule seulement l'affichage du brouillon (antennes A1..AN, signal.json générés,
 * motifs de blocage) à chaque changement de la disposition.
 */

export const FREQUENCY_LIMITS = {
  /** 12 freq_delta et 12 colonnes calib au plus, donc au plus 12 antennes par site. */
  maxAntennasPerSite: 12,
  /** 4 DAC par gateway. */
  maxAntennasPerGateway: 4,
  /** 200 fréquences par DAC ; une antenne = un DAC avec toute la liste. */
  maxFrequencies: 200,
  minHz: 60_000_000,
  maxHz: 500_000_000,
  thetaMax: 3600,
} as const;

// ── Formats d'affichage (maquette validée) ──────────────────────────────────────────────

/** `1234567` → `1 234 567`. */
export function groupThousands(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** Hz → MHz à la virgule (`140,453504`), `digits` décimales. */
export function formatMHz(hz: number, digits = 6): string {
  return (hz / 1e6).toFixed(digits).replace('.', ',');
}

/** theta 0-3600 (dixièmes de degré) → `140,9°`, indicatif. */
export function formatThetaDegrees(theta: number): string {
  return (theta / 10).toFixed(1).replace('.', ',') + '°';
}

/** freq_delta affiché : `+3 456 Hz`, `0 Hz` (le signe moins reste lisible pour un écart refusé). */
export function formatDelta(delta: number): string {
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  return `${sign}${groupThousands(Math.abs(delta))} Hz`;
}

// ── Résumé, avertissements ──────────────────────────────────────────────────────────────

export interface FrequencyFileStats {
  count: number;
  minHz: number;
  maxHz: number;
  deltaCount: number;
  thetaMin: number;
  thetaMax: number;
}

export function frequencyFileStats(file: FrequencyFile): FrequencyFileStats {
  const theta = file.theta.length ? file.theta : [0];
  const freq = file.freq.length ? file.freq : [0];
  return {
    count: file.freq.length,
    minHz: Math.min(...freq),
    maxHz: Math.max(...freq),
    deltaCount: file.freq_delta.length,
    thetaMin: Math.min(...theta),
    thetaMax: Math.max(...theta),
  };
}

/** Avertissements non bloquants sur `freq` : liste non triée, doublons (badges jaunes). */
export interface FrequencyListWarnings {
  /** Premier index k tel que freq[k] < freq[k-1], sinon null. */
  unsortedAt: number | null;
  /** Nombre de valeurs répétées. */
  duplicates: number;
}

export function frequencyListWarnings(freq: readonly number[]): FrequencyListWarnings {
  const k = freq.findIndex((f, i) => i > 0 && f < freq[i - 1]);
  return { unsortedAt: k > 0 ? k : null, duplicates: freq.length - new Set(freq).size };
}

/** Clé transloco + paramètres d'un badge jaune (avertissement non bloquant). */
export interface FrequencyBadge {
  key: string;
  params?: Record<string, unknown>;
}

const BADGE_KEY = 'siteConfiguration.frequency.badge';

/**
 * Badge d'un avertissement serveur (`FrequencyWarningCode`). Le `message` du serveur, déjà chiffré,
 * reste affiché en infobulle ; le code ne sert qu'à la présentation.
 */
export function warningBadge(w: Pick<FrequencyWarning, 'code' | 'count'>): FrequencyBadge {
  const count = w.count ?? 1;
  switch (w.code) {
    case 'FREQ_UNSORTED':
      return { key: `${BADGE_KEY}.unsorted` };
    case 'FREQ_DUPLICATES':
      return { key: `${BADGE_KEY}.duplicates`, params: { count } };
    case 'FREQ_DELTA_IGNORED':
      return { key: `${BADGE_KEY}.deltaIgnored`, params: { count } };
    case 'CALIB_COLUMNS_IGNORED':
      return { key: `${BADGE_KEY}.columnsIgnored`, params: { count } };
    case 'UNKNOWN_KEY_IGNORED':
      return { key: `${BADGE_KEY}.unknownKey`, params: { count } };
    case 'LEGACY_THETA_COLUMNS_DIFFER':
      return { key: `${BADGE_KEY}.thetaColumns` };
    default:
      return { key: `${BADGE_KEY}.other` };
  }
}

// ── Mise en forme et export ─────────────────────────────────────────────────────────────

/** Mise en forme de l'exemple (`exemple-frequences.json`) : 8 fréquences et 10 theta par ligne. */
export function prettyFrequencyFile(file: FrequencyFile): string {
  const rows = (a: readonly number[], per: number): string => {
    const out: string[] = [];
    for (let i = 0; i < a.length; i += per) out.push('    ' + a.slice(i, i + per).join(', '));
    return out.join(',\n');
  };
  return (
    `{\n  "freq": [\n${rows(file.freq, 8)}\n  ],\n` +
    `  "freq_delta": [${file.freq_delta.join(', ')}],\n` +
    `  "theta": [\n${rows(file.theta, 10)}\n  ]\n}\n`
  );
}

/** Couverture non ambiguë : c / pgcd des écarts entre fréquences consécutives, en mètres. */
export function frequencyCoverage(freq: readonly number[]): number | null {
  if (freq.length < 2) return null;
  const gcd = (a: number, b: number): number => {
    let x = Math.abs(a);
    let y = Math.abs(b);
    while (y) [x, y] = [y, x % y];
    return x;
  };
  let g = 0;
  for (let i = 1; i < freq.length; i++) {
    const d = Math.round(freq[i] - freq[i - 1]);
    if (d <= 0) return null;
    g = gcd(g, d);
  }
  if (!g) return null;
  return Math.round((299_792_458 / g) * 10) / 10;
}

// ── Calib ───────────────────────────────────────────────────────────────────────────────

/** Lignes et colonnes d'un calib (une ligne par fréquence, une cellule par antenne). */
export interface CalibShape {
  rows: number;
  columns: number;
}

/** Forme lue localement pour l'aperçu ; le serveur contrôle le contenu (cellules, paires…). */
export function calibShape(raw: string): CalibShape {
  const lines = raw.split(/\r?\n/).filter(l => l.trim() !== '');
  return { rows: lines.length, columns: lines.length ? lines[0].split(',').length : 0 };
}

/** Cellules d'un calib, ligne par ligne, pour l'aperçu lisible. */
export function calibCells(raw: string, maxRows = Infinity): string[][] {
  return raw
    .split(/\r?\n/)
    .filter(l => l.trim() !== '')
    .slice(0, maxRows)
    .map(l => l.split(',').map(c => c.trim()));
}

/** Cellule réelle `a` (lue a+0i). */
export function isRealCalibCell(cell: string): boolean {
  return /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(cell);
}

// ── Disposition : antennes A1..AN, signal.json générés ──────────────────────────────────

/** Disposition en cours d'édition : gateways dans l'ordre du site, antennes dans l'ordre. */
export interface FrequencyLayoutGateway {
  name: string;
  deviceId?: string;
  /** RF OUT choisi (0..3) ou null (automatique = rang de l'antenne dans la gateway). */
  antennas: ReadonlyArray<{ rfOut: number | null }>;
}

export interface SiteAntennaView {
  /** 0 pour A1. */
  index: number;
  label: string;
  gatewayIndex: number;
  gatewayName: string;
  antennaOrder: number;
  dac: number;
  dacPinned: boolean;
  /** freq_delta appliqué ; null si le fichier en a moins que d'antennes. */
  freqDelta: number | null;
  firstHz: number | null;
  lastHz: number | null;
  /** Colonne calib (1-based) ; null si le calib a moins de colonnes. */
  calibColumn: number | null;
  /** Deux antennes de la même gateway sur ce DAC : la seconde est en conflit. */
  dacConflict: boolean;
}

export function antennaCount(gateways: readonly FrequencyLayoutGateway[]): number {
  return gateways.reduce((n, g) => n + g.antennas.length, 0);
}

export function siteAntennas(
  gateways: readonly FrequencyLayoutGateway[],
  file: FrequencyFile | null,
  calibColumns: number | null
): SiteAntennaView[] {
  const out: SiteAntennaView[] = [];
  let index = 0;
  gateways.forEach((g, gi) => {
    const used = new Set<number>();
    g.antennas.forEach((a, ai) => {
      const dac = a.rfOut ?? ai;
      const delta = file && index < file.freq_delta.length ? file.freq_delta[index] : null;
      const first = file && file.freq.length ? file.freq[0] : null;
      const last = file && file.freq.length ? file.freq[file.freq.length - 1] : null;
      out.push({
        index,
        label: `A${index + 1}`,
        gatewayIndex: gi,
        gatewayName: g.name,
        antennaOrder: ai,
        dac,
        dacPinned: a.rfOut != null,
        freqDelta: delta,
        firstHz: delta != null && first != null ? first + delta : null,
        lastHz: delta != null && last != null ? last + delta : null,
        calibColumn: calibColumns != null && index < calibColumns ? index + 1 : null,
        dacConflict: used.has(dac),
      });
      used.add(dac);
      index++;
    });
  });
  return out;
}

/** signal.json tel que publié sur `gw/v3/{deviceId}/to_gw/signal`, sans `amp_div_0..3`. */
export interface SignalJsonContent {
  size: number;
  dac0: Array<[number, number]>;
  dac1: Array<[number, number]>;
  dac2: Array<[number, number]>;
  dac3: Array<[number, number]>;
}

export interface GeneratedSignalView {
  gatewayIndex: number;
  gatewayName: string;
  deviceId?: string;
  fileName: string;
  signal: SignalJsonContent;
  /** Antenne (0 pour A1) qui remplit chaque DAC, null si le DAC reste vide. */
  dacAntenna: Array<number | null>;
  /** Couples d'antennes (0-based) visant le même DAC de cette gateway. */
  conflicts: Array<{ dac: number; first: number; second: number }>;
  /** Antennes (0-based) sans freq_delta : leur DAC reste vide. */
  missingDelta: number[];
  entries: number;
}

type DacKey = 'dac0' | 'dac1' | 'dac2' | 'dac3';
const DAC_KEYS: readonly DacKey[] = ['dac0', 'dac1', 'dac2', 'dac3'];

/** Nom d'export d'un signal.json généré : `<SLUG>_GW<n>_signal.json`. */
export function signalFileName(slug: string, gatewayIndex: number): string {
  return `${slug || 'site'}_GW${gatewayIndex}_signal.json`;
}

/**
 * Un signal.json par gateway : chaque antenne remplit son DAC avec toute la liste
 * `[freq[k] + freq_delta[i], theta[k]]` ; theta recopié sans conversion.
 */
export function generateSignals(
  slug: string,
  gateways: readonly FrequencyLayoutGateway[],
  file: FrequencyFile,
  size: number
): GeneratedSignalView[] {
  let index = 0;
  return gateways.map((g, gi) => {
    const signal: SignalJsonContent = { size, dac0: [], dac1: [], dac2: [], dac3: [] };
    const dacAntenna: Array<number | null> = [null, null, null, null];
    const conflicts: GeneratedSignalView['conflicts'] = [];
    const missingDelta: number[] = [];
    g.antennas.forEach((a, ai) => {
      const idx = index++;
      const dac = a.rfOut ?? ai;
      const delta = idx < file.freq_delta.length ? file.freq_delta[idx] : undefined;
      const holder = dacAntenna[dac];
      if (holder != null) {
        conflicts.push({ dac, first: holder, second: idx });
        return;
      }
      dacAntenna[dac] = idx;
      if (delta === undefined) {
        missingDelta.push(idx);
        return;
      }
      signal[DAC_KEYS[dac]] = file.freq.map((f, k) => [f + delta, file.theta[k]] as [number, number]);
    });
    return {
      gatewayIndex: gi,
      gatewayName: g.name,
      deviceId: g.deviceId,
      fileName: signalFileName(slug, gi),
      signal,
      dacAntenna,
      conflicts,
      missingDelta,
      entries: DAC_KEYS.reduce((n, k) => n + signal[k].length, 0),
    };
  });
}

/** JSON exporté (MA, configuration en direct) : compact, clés dans l'ordre du firmware. */
export function signalJsonText(s: SignalJsonContent): string {
  return JSON.stringify({ size: s.size, dac0: s.dac0, dac1: s.dac1, dac2: s.dac2, dac3: s.dac3 });
}

/** Aperçu lisible d'un signal.json, `perDac` entrées au plus par DAC. */
export function prettySignalJson(s: SignalJsonContent, perDac = Infinity, moreLabel = 'entrées de plus'): string {
  const arr = (a: Array<[number, number]>): string =>
    a.length
      ? '[\n' +
        a
          .slice(0, perDac)
          .map(e => `    [${e[0]}, ${e[1]}]`)
          .join(',\n') +
        (a.length > perDac ? `,\n    … ${a.length - perDac} ${moreLabel}` : '') +
        '\n  ]'
      : '[]';
  return `{\n  "size": ${s.size},\n  "dac0": ${arr(s.dac0)},\n  "dac1": ${arr(s.dac1)},\n  "dac2": ${arr(
    s.dac2
  )},\n  "dac3": ${arr(s.dac3)}\n}`;
}

// ── Motifs de blocage de la validation (brouillon) ──────────────────────────────────────

/** Motif bloquant : code du contrat (`FrequencyIssueCode`) et paramètres du message traduit. */
export interface FrequencyBlocker {
  code:
    | 'FREQ_FILE_MISSING'
    | 'FREQ_DELTA_TOO_FEW'
    | 'SITE_TOO_MANY_ANTENNAS'
    | 'GATEWAY_TOO_MANY_ANTENNAS'
    | 'CALIB_MISSING'
    | 'CALIB_ROW_COUNT_MISMATCH'
    | 'CALIB_TOO_FEW_COLUMNS'
    | 'DAC_SHARED'
    | 'PRESET_TOO_FEW_ANTENNAS';
  params: Record<string, string | number>;
}

export interface FrequencySetupDraft {
  gateways: readonly FrequencyLayoutGateway[];
  file: FrequencyFile | null;
  fileName: string | null;
  calib: (CalibShape & { fileName: string }) | null;
  /** Preset choisi (source « Depuis un preset ») : jusqu'à `maxAntennas` antennes. */
  preset?: { name: string; maxAntennas: number | null } | null;
}

/**
 * Motifs qui bloqueraient la validation (`PATCH /sites/{id}/layout`, 422
 * `FREQUENCY_SETUP_INVALID`) avec la disposition en cours d'édition. Mêmes règles que le serveur
 * (ADR-039 § 2, 3, 4, 8) ; le serveur reste juge à la validation.
 */
export function frequencySetupBlockers(d: FrequencySetupDraft): FrequencyBlocker[] {
  const out: FrequencyBlocker[] = [];
  const n = antennaCount(d.gateways);
  if (!d.file) out.push({ code: 'FREQ_FILE_MISSING', params: {} });
  if (d.preset && d.preset.maxAntennas != null && n > d.preset.maxAntennas) {
    out.push({ code: 'PRESET_TOO_FEW_ANTENNAS', params: { name: d.preset.name, max: d.preset.maxAntennas, count: n } });
  }
  if (d.file && d.file.freq_delta.length < n) {
    const nd = d.file.freq_delta.length;
    out.push({
      code: 'FREQ_DELTA_TOO_FEW',
      params: { count: n, deltas: nd, fileName: d.fileName ?? '', first: `A${nd + 1}`, last: `A${n}` },
    });
  }
  if (n > FREQUENCY_LIMITS.maxAntennasPerSite) {
    out.push({ code: 'SITE_TOO_MANY_ANTENNAS', params: { count: n, max: FREQUENCY_LIMITS.maxAntennasPerSite } });
  }
  d.gateways.forEach((g, gi) => {
    if (g.antennas.length > FREQUENCY_LIMITS.maxAntennasPerGateway) {
      out.push({
        code: 'GATEWAY_TOO_MANY_ANTENNAS',
        params: {
          gateway: `GW${gi}`,
          name: g.name,
          count: g.antennas.length,
          max: FREQUENCY_LIMITS.maxAntennasPerGateway,
        },
      });
    }
  });
  if (!d.calib) out.push({ code: 'CALIB_MISSING', params: {} });
  else if (d.file && d.calib.rows !== d.file.freq.length) {
    out.push({
      code: 'CALIB_ROW_COUNT_MISMATCH',
      params: { fileName: d.calib.fileName, rows: d.calib.rows, count: d.file.freq.length },
    });
  } else if (d.calib.columns < n) {
    out.push({
      code: 'CALIB_TOO_FEW_COLUMNS',
      params: { fileName: d.calib.fileName, columns: d.calib.columns, count: n },
    });
  }
  const antennas = siteAntennas(d.gateways, null, null);
  for (const a of antennas) {
    if (!a.dacConflict) continue;
    const first = antennas.find(b => b.gatewayIndex === a.gatewayIndex && b.dac === a.dac && b.index < a.index);
    out.push({
      code: 'DAC_SHARED',
      params: { first: first?.label ?? '', second: a.label, gateway: `GW${a.gatewayIndex}`, dac: a.dac },
    });
  }
  return out;
}

/** Jusqu'à N antennes : min(freq_delta, colonnes calib) ; null sans fichier ou sans calib. */
export function presetMaxAntennas(
  file: FrequencyFile | null | undefined,
  calibColumns: number | null | undefined
): number | null {
  if (!file || calibColumns == null) return null;
  return Math.min(file.freq_delta.length, calibColumns);
}

/** Texte d'un fichier choisi (FileReader : `File.text()` manque dans certains environnements). */
export function readFileText(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

/** Motif du serveur inséré au milieu d'une phrase : sans point final (pas de « .. »). */
export function reasonClause(reason: string): string {
  return reason.replace(/[\s.]+$/, '');
}
